import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'

import { CancelableRequest } from 'api/common/types'

// axios >= 1.19 types every request method as Promise<AxiosResponseResult<T, R, D, P>>, a conditional
// type that only resolves once R is concrete. Inside these generic wrappers R is still a type
// parameter, so the result cannot be assigned to Promise<R> without the assertion. At every call site
// R is concrete and resolves to exactly what axios returns, so the assertion is sound.
export class ApiInstance {
  client: AxiosInstance

  constructor(config?: AxiosRequestConfig) {
    const client = axios.create(config)

    this.client = client
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): CancelableRequest<R> {
    return (signal?: AbortSignal) =>
      this.client.get<T, R>(url, {
        ...config,
        signal,
      }) as Promise<R>
  }

  post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): CancelableRequest<R> {
    return (signal?: AbortSignal) =>
      this.client.post<T, R>(url, data, {
        ...config,
        signal,
      }) as Promise<R>
  }

  put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): CancelableRequest<R> {
    return (signal?: AbortSignal) =>
      this.client.put<T, R>(url, data, {
        ...config,
        signal,
      }) as Promise<R>
  }

  patch<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): CancelableRequest<R> {
    return (signal?: AbortSignal) =>
      this.client.patch<T, R>(url, data, {
        ...config,
        signal,
      }) as Promise<R>
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): CancelableRequest<R> {
    return (signal?: AbortSignal) =>
      this.client.delete<T, R>(url, {
        ...config,
        signal,
      }) as Promise<R>
  }
}

export const createApi = (config?: AxiosRequestConfig) => new ApiInstance(config)
